import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Building, Edit, Trash2, Eye, Filter, LayoutGrid, Table, Maximize, Minimize, TrendingUp, TrendingDown, X } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchInvestibleStructures,
  createInvestibleStructure,
  updateInvestibleStructure,
  deleteInvestibleStructure,
  setFilters,
  clearError
} from '@/store/slices/investibleStructuresSlice';
import { fetchPlanningPeriods } from '@/store/slices/planningPeriodsSlice';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassSelect,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  ProgressBar,
  CollapsibleMetricsSection,
} from '@/design-system';
import { DataTable } from '@/components/ui';
import InvestibleStructureForm from './components/InvestibleStructureForm';
import InvestibleStructureDetailModal from './components/InvestibleStructureDetailModal';
import type { InvestibleStructure, ISCategory, Column, User, UserRole } from '@/types';
import { usersApi } from '@/utils/api';
import { formatCurrencyCompact } from '@/utils/formatters';

const InvestibleStructuresPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    structures,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    filters
  } = useSelector((state: RootState) => state.investibleStructures);

  const { periods } = useSelector((state: RootState) => state.planningPeriods);
  const { user } = useSelector((state: RootState) => state.auth);

  // Role-based display logic
  const userRole = user?.role as UserRole | undefined;
  const showMetrics = ['ADMIN', 'PMO', 'FINANCE', 'EXECUTIVE'].includes(userRole || '');
  const isISOwner = userRole === 'IS_OWNER';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState<InvestibleStructure | null>(null);
  // Default to grid view for IS_OWNER, table view for others
  const [viewMode, setViewMode] = useState<'grid' | 'table'>(isISOwner ? 'grid' : 'table');
  const [showFilters, setShowFilters] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  // IS_OWNER filtering: auto-filter by their user ID, clear for non-IS_OWNER
  useEffect(() => {
    if (isISOwner && user?.id) {
      // IS_OWNER: ensure ownerId filter is set to current user
      if (filters.ownerId !== user.id) {
        dispatch(setFilters({ ...filters, ownerId: user.id }));
      }
    } else if (!isISOwner && filters.ownerId) {
      // Non-IS_OWNER: clear the ownerId filter if present
      const { ownerId, ...restFilters } = filters;
      dispatch(setFilters(restFilters));
    }
  }, [isISOwner, user?.id, dispatch, filters.ownerId]);

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
    dispatch(fetchInvestibleStructures(filters));
  }, [dispatch, filters, user?.id]);

  useEffect(() => {
    // IS_OWNER doesn't need the users list (they only see their own ISs)
    // and they lack VIEW_USERS permission, so skip the fetch to avoid 403
    if (isISOwner) return;

    const loadUsers = async (): Promise<void> => {
      const response = await usersApi.getAll({ isActive: true });
      if (response.success && response.data) {
        setUsers((response.data as any).users || response.data);
      }
    };
    void loadUsers();
  }, [isISOwner]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  // Calculate metrics from structures data
  const metrics = useMemo(() => {
    const total = structures.length;
    const demandGreaterThanSupply = structures.filter(s =>
      (s.demandTeamWeeks || 0) > (s.supplyTeamWeeks || 0)
    ).length;
    const supplyGreaterThanDemand = structures.filter(s =>
      (s.supplyTeamWeeks || 0) > (s.demandTeamWeeks || 0)
    ).length;
    return { total, demandGreaterThanSupply, supplyGreaterThanDemand };
  }, [structures]);

  const handleCreateStructure = async (data: Partial<InvestibleStructure>) => {
    const result = await dispatch(createInvestibleStructure(data));
    if (createInvestibleStructure.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateStructure = async (data: Partial<InvestibleStructure>) => {
    if (!selectedStructure) return;
    const result = await dispatch(updateInvestibleStructure({ id: selectedStructure.id, data }));
    if (updateInvestibleStructure.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedStructure(null);
    }
  };

  const handleDeleteStructure = async () => {
    if (!selectedStructure) return;
    const result = await dispatch(deleteInvestibleStructure(selectedStructure.id));
    if (deleteInvestibleStructure.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedStructure(null);
    }
  };

  const handleViewDetails = (structure: InvestibleStructure) => {
    setSelectedStructure(structure);
    setIsDetailModalOpen(true);
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      (newFilters as any)[key] = value;
    } else {
      delete (newFilters as any)[key];
    }
    // Preserve ownerId filter for IS_OWNER role
    if (isISOwner && user?.id) {
      newFilters.ownerId = user.id;
    }
    dispatch(setFilters(newFilters));
  };

  const getCategoryBadgeVariant = (category: ISCategory): 'primary' | 'info' | 'success' => {
    switch (category) {
      case 'BUSINESS':
        return 'primary';
      case 'TECH':
        return 'info';
      case 'SUPPORT':
        return 'success';
      default:
        return 'primary';
    }
  };

  const getOwnerDisplayName = (structure: InvestibleStructure) => {
    if (structure.ownerType === 'USER' && structure.owner) {
      return `${structure.owner.firstName} ${structure.owner.lastName}`;
    } else if (structure.ownerType === 'TEXT' && structure.ownerText) {
      return structure.ownerText;
    }
    return 'Unassigned';
  };

  const calculateUtilization = (structure: InvestibleStructure): number => {
    const demand = structure.demandTeamWeeks || 0;
    const supply = structure.supplyTeamWeeks || 0;
    if (demand === 0) return 0;
    return Math.min(100, (supply / demand) * 100);
  };

  const getUtilizationColor = (structure: InvestibleStructure): 'emerald' | 'amber' | 'rose' => {
    const util = calculateUtilization(structure);
    if (util >= 90) return 'emerald';
    if (util >= 70) return 'amber';
    return 'rose';
  };

  const planningPeriodOptions = [
    { value: '', label: 'All Planning Periods' },
    ...periods.map(period => ({
      value: period.id,
      label: period.name
    }))
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'BUSINESS', label: 'Business' },
    { value: 'TECH', label: 'Tech' },
    { value: 'SUPPORT', label: 'Support' }
  ];

  const ownerOptions = [
    { value: '', label: 'All Owners' },
    ...users.map(user => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`
    }))
  ];

  // Table columns for DataTable view
  const columns: Column<InvestibleStructure>[] = [
    {
      key: 'name',
      header: 'Investible Structure',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground text-[13px]">{value}</div>
          {row.description && (
            <div className="text-xs text-muted-foreground/60 truncate max-w-xs">
              {row.description}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (value) => (
        <GlassBadge variant={getCategoryBadgeVariant(value)} size="sm">
          {value}
        </GlassBadge>
      )
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (_value, row) => (
        <span className="text-muted-foreground">{getOwnerDisplayName(row)}</span>
      )
    },
    {
      key: '_count',
      header: 'EPICs',
      render: (value) => (
        <span className="font-medium text-accent-blue">{value?.epics || 0}</span>
      )
    },
    {
      key: 'demandTeamWeeks',
      header: 'Demand (TW)',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-status-warning">
          {value || 0}
        </span>
      )
    },
    {
      key: 'supplyTeamWeeks',
      header: 'Supply',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-status-success">
            {value || 0} TW
          </div>
          <div className="text-xs text-muted-foreground/60">
            {formatCurrencyCompact(row.supplyAnnualCost || 0)}
          </div>
        </div>
      )
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (value) => (
        <span className="text-muted-foreground/60 text-sm">
          {new Date(value).toLocaleDateString()}
        </span>
      )
    }
  ];

  const tableActions = [
    // Only show View action for non-IS_OWNER users
    ...(!isISOwner ? [{
      label: 'View',
      icon: Eye,
      onClick: (structure: InvestibleStructure) => {
        handleViewDetails(structure);
      }
    }] : []),
    {
      label: 'Edit',
      icon: Edit,
      onClick: (structure: InvestibleStructure) => {
        setSelectedStructure(structure);
        setIsEditModalOpen(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (structure: InvestibleStructure) => {
        setSelectedStructure(structure);
        setIsDeleteModalOpen(true);
      }
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading investible structures..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        {!isMaximized && (
          <SectionReveal>
            <GlassPageHeader
              title="Investible Structures"
              subtitle="Manage business areas and investment priorities"
              icon={<Building className="w-6 h-6" />}
              iconGradient="blue-violet"
              actions={
                <div className="flex items-center gap-2">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowFilters(true)}
                    leftIcon={<Filter className="w-4 h-4" />}
                  >
                    Filters
                  </GlassButton>
                  {/* Hide view toggle for IS_OWNER (always Grid view) */}
                  {!isISOwner && (
                    <>
                      <GlassButton
                        variant={viewMode === 'grid' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setViewMode('grid')}
                        leftIcon={<LayoutGrid className="w-4 h-4" />}
                      >
                        Grid
                      </GlassButton>
                      <GlassButton
                        variant={viewMode === 'table' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setViewMode('table')}
                        leftIcon={<Table className="w-4 h-4" />}
                      >
                        Table
                      </GlassButton>
                    </>
                  )}
                  {viewMode === 'table' && !isMaximized && !isISOwner && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setIsMaximized(true)}
                      leftIcon={<Maximize className="w-4 h-4" />}
                    >
                      Maximize
                    </GlassButton>
                  )}
                </div>
              }
            />
          </SectionReveal>
        )}

        {/* Error Display */}
        {!isMaximized && error && (
          <SectionReveal delay={0.05}>
            <GlassAlert
              variant="error"
              message={error}
              onClose={() => dispatch(clearError())}
            />
          </SectionReveal>
        )}

        {/* Metrics Section - Only for Admin/PMO/Finance/Executive */}
        {!isMaximized && showMetrics && (
          <SectionReveal delay={0.1}>
            <CollapsibleMetricsSection storageKey="investible-structures">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                  title="Total Investible Structures"
                  value={metrics.total}
                  icon={<Building className="w-5 h-5" />}
                  iconColor="violet"
                />
                <MetricCard
                  title="Demand > Supply"
                  value={metrics.demandGreaterThanSupply}
                  subtitle="Under-resourced"
                  icon={<TrendingUp className="w-5 h-5" />}
                  iconColor="rose"
                />
                <MetricCard
                  title="Supply > Demand"
                  value={metrics.supplyGreaterThanDemand}
                  subtitle="Over-resourced"
                  icon={<TrendingDown className="w-5 h-5" />}
                  iconColor="emerald"
                />
              </div>
            </CollapsibleMetricsSection>
          </SectionReveal>
        )}

        {/* Filters Drawer */}
        {!isMaximized && (
          <div
            className={`fixed inset-0 z-40 transition ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'}`}
            aria-hidden={!showFilters}
          >
            {/* Backdrop */}
            <div
              className={`absolute inset-0 bg-black/25 backdrop-blur-sm transition-opacity ${showFilters ? 'opacity-100' : 'opacity-0'}`}
              onClick={() => setShowFilters(false)}
            />
            {/* Panel */}
            <div
              className={`absolute right-0 top-0 h-full w-80 glass-strong border-l border-white/20 shadow-xl transition-transform ${
                showFilters ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Filters</h4>
                  <p className="text-xs text-muted-foreground/60">Refine Investible Structures</p>
                </div>
                <GlassButton variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                  <X className="w-4 h-4" />
                </GlassButton>
              </div>
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <span className="text-xs uppercase text-muted-foreground/60 font-medium tracking-wide">Planning Period</span>
                  <GlassSelect
                    value={filters.planningPeriodId || ''}
                    onChange={(e) => handleFilterChange('planningPeriodId', e.target.value)}
                    options={planningPeriodOptions}
                  />
                </div>
                <div className="space-y-2">
                  <span className="text-xs uppercase text-muted-foreground/60 font-medium tracking-wide">Category</span>
                  <GlassSelect
                    value={filters.category || ''}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    options={categoryOptions}
                  />
                </div>
                {/* Hide owner filter for IS_OWNER since they only see their own */}
                {!isISOwner && (
                  <div className="space-y-2">
                    <span className="text-xs uppercase text-muted-foreground/60 font-medium tracking-wide">Owner</span>
                    <GlassSelect
                      value={filters.ownerId || ''}
                      onChange={(e) => handleFilterChange('ownerId', e.target.value)}
                      options={ownerOptions}
                    />
                  </div>
                )}
                <div className="pt-2">
                  <GlassButton
                    variant="secondary"
                    onClick={() => {
                      const clearedFilters: typeof filters = {};
                      // Preserve ownerId for IS_OWNER
                      if (isISOwner && user?.id) {
                        clearedFilters.ownerId = user.id;
                      }
                      dispatch(setFilters(clearedFilters));
                    }}
                    leftIcon={<Filter className="w-4 h-4" />}
                    fullWidth
                  >
                    Clear Filters
                  </GlassButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {!isMaximized && viewMode === 'grid' ? (
          /* Grid View */
          <SectionReveal delay={showMetrics ? 0.15 : 0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {structures.length === 0 ? (
                <div className="col-span-full">
                  <GlassCard padding="lg" className="text-center">
                    <Building className="w-12 h-12 text-muted-foreground/60 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No Investible Structures</h3>
                    <p className="text-muted-foreground/60">
                      {isISOwner
                        ? 'You have no assigned investible structures yet.'
                        : 'Create your first investible structure to get started.'
                      }
                    </p>
                  </GlassCard>
                </div>
              ) : (
                structures.map((structure) => (
                  <GlassCard key={structure.id} hover padding="none" className="flex flex-col h-full">
                    <div className="p-5 flex-1">
                      {/* Header: Title + Category Badge */}
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-semibold text-foreground text-base line-clamp-2 flex-1 mr-2">
                          {structure.name}
                        </h3>
                        <GlassBadge variant={getCategoryBadgeVariant(structure.category)} size="sm">
                          {structure.category}
                        </GlassBadge>
                      </div>

                      {/* Description */}
                      {structure.description && (
                        <p className="text-sm text-muted-foreground/60 mb-4 line-clamp-2">
                          {structure.description}
                        </p>
                      )}

                      {/* Data Rows */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground/60">Owner</span>
                          <span className="text-muted-foreground">{getOwnerDisplayName(structure)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground/60">EPICs</span>
                          <span className="text-accent-blue font-medium">
                            {structure._count?.epics || 0}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground/60">Demand</span>
                          <span className="text-status-warning font-medium">
                            {structure.demandTeamWeeks || 0} TW
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground/60">Supply</span>
                          <div className="text-right">
                            <span className="text-status-success font-medium">
                              {structure.supplyTeamWeeks || 0} TW
                            </span>
                            <div className="text-xs text-muted-foreground/60">
                              {formatCurrencyCompact(structure.supplyAnnualCost || 0)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Capacity Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-muted-foreground/60 mb-1">
                          <span>Capacity Utilization</span>
                          <span>{calculateUtilization(structure).toFixed(0)}%</span>
                        </div>
                        <ProgressBar
                          value={calculateUtilization(structure)}
                          color={getUtilizationColor(structure)}
                          size="sm"
                          showShimmer
                          gradient
                        />
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="px-5 py-3 border-t border-white/10 dark:border-white/5 flex justify-between">
                      <div className="flex gap-1">
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedStructure(structure);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedStructure(structure);
                            setIsDeleteModalOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </GlassButton>
                      </div>
                      {/* Only show View button for non-IS_OWNER users */}
                      {!isISOwner && (
                        <GlassButton
                          variant="secondary"
                          size="sm"
                          leftIcon={<Eye className="w-4 h-4" />}
                          onClick={() => handleViewDetails(structure)}
                        >
                          View
                        </GlassButton>
                      )}
                    </div>
                  </GlassCard>
                ))
              )}
            </div>
          </SectionReveal>
        ) : null}

        {/* Maximized View - Outside SectionReveal to avoid stacking context */}
        {isMaximized && (
          <>
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
              onClick={() => setIsMaximized(false)}
            />
            <GlassCard
              variant="strong"
              className="fixed inset-4 z-[60] flex flex-col overflow-hidden"
              padding="none"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text)]">Investible Structures</h3>
                  <p className="text-sm text-[var(--muted)]">{structures.length} structures</p>
                </div>
                <GlassButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsMaximized(false)}
                  leftIcon={<Minimize className="w-4 h-4" />}
                >
                  Minimize
                </GlassButton>
              </div>
              <div className="flex-1 overflow-auto">
                <DataTable
                  data={structures}
                  columns={columns}
                  actions={tableActions}
                  searchable
                  sortable
                  pagination
                  pageSize={50}
                  emptyMessage="No investible structures found"
                />
              </div>
            </GlassCard>
          </>
        )}

        {/* Normal Table View - Inside SectionReveal with scroll container */}
        {!isMaximized && viewMode === 'table' && (
          <SectionReveal delay={showMetrics ? 0.2 : 0.15}>
            <GlassCard variant="default" padding="none">
              <div className="max-h-[calc(100vh-350px)] overflow-auto">
                <DataTable
                  data={structures}
                  columns={columns}
                  actions={tableActions}
                  searchable
                  sortable
                  pagination
                  pageSize={15}
                  emptyMessage="No investible structures found"
                />
              </div>
            </GlassCard>
          </SectionReveal>
        )}

        {/* Floating New Structure button */}
        {!isMaximized && (
          <button
            className="fixed bottom-6 right-6 h-14 w-14 rounded-2xl shadow-lg
                       bg-gradient-to-r from-violet-500 to-violet-600
                       text-white hover:shadow-xl hover:scale-105
                       transition-all duration-200 flex items-center justify-center
                       focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
            onClick={() => setIsCreateModalOpen(true)}
            aria-label="Create Investible Structure"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}

        {/* Create Modal */}
        <GlassModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          title="Create Investible Structure"
          subtitle="Add a new business area or investment priority"
          icon={<Building className="w-5 h-5" />}
          iconGradient="blue-violet"
          size="lg"
        >
          <InvestibleStructureForm
            planningPeriods={periods}
            onSubmit={handleCreateStructure}
            onCancel={() => setIsCreateModalOpen(false)}
            isLoading={isCreating}
          />
        </GlassModal>

        {/* Edit Modal */}
        <GlassModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedStructure(null);
          }}
          title="Edit Investible Structure"
          subtitle={selectedStructure?.name}
          icon={<Edit className="w-5 h-5" />}
          iconGradient="cyan"
          size="lg"
        >
          <InvestibleStructureForm
            initialData={selectedStructure}
            planningPeriods={periods}
            onSubmit={handleUpdateStructure}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedStructure(null);
            }}
            isLoading={isUpdating}
          />
        </GlassModal>

        {/* Delete Confirmation Modal */}
        <GlassConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedStructure(null);
          }}
          onConfirm={handleDeleteStructure}
          title="Delete Investible Structure"
          message={`Are you sure you want to delete "${selectedStructure?.name}"? This will also delete all associated EPICs and cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          isLoading={isDeleting}
        />

        {/* Detail Modal */}
        <InvestibleStructureDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedStructure(null);
          }}
          structure={selectedStructure}
          onEdit={() => {
            setIsDetailModalOpen(false);
            setIsEditModalOpen(true);
          }}
        />
      </div>
    </div>
  );
};

export default InvestibleStructuresPage;
